import { CheckboxFormField, IFormRender, Theme } from '@busybusy/webapp-react-ui';
import DashboardRibbon from 'components/foundation/dashboard/DashboardRibbon/DashboardRibbon';
import Typography from 'components/foundation/text/Typography/Typography';
import HeaderText from 'containers/settings/components/HeaderText/HeaderText';
import SectionHeader from 'containers/settings/components/SectionHeader/SectionHeader';
import BudgetFormFields from 'containers/settings/positions/components/BudgetFormFields/BudgetFormFields';
import CertificationsFormField from 'containers/settings/positions/components/CertificationsFormField/CertificationsFormField';
import {
  POSITION_MANAGE_PROJECT_PLAN_DETAIL_DESCRIPTION,
  POSITION_MANAGE_PROJECT_PLAN_DETAIL_TITLE,
} from 'containers/settings/positions/details/ManageProjectPlansDetail';
import { POSITION_MASK_EVERYONE } from 'containers/settings/positions/util/util';
import useProjectPlanningIsEnabled from 'hooks/feature/project-planning/useProjectPlanningIsEnabled/useProjectPlanningIsEnabled';
import { compact } from 'lodash';
import { useFeatureFlags } from 'utils/features';
import { t } from 'utils/localize';
import { PositionManageBudgetType } from 'utils/positionUtils';
import { ICreatePositionsFormData } from '../CreatePositionForm';

export interface ICreatePositionCompanyManagementSectionProps {
  form: IFormRender<ICreatePositionsFormData>;
}

const CreatePositionCompanyManagementSection = ({ form }: ICreatePositionCompanyManagementSectionProps) => {
  const dailyReportIsEnabled = useFeatureFlags('DAILY_REPORTS');
  const isBulletinEnabled = useFeatureFlags('BULLETIN_FEATURE');
  const isCertificationsEnabled = useFeatureFlags('CERTIFICATIONS');
  const isEquipmentInspectionsEnabled = useFeatureFlags('EQUIPMENT_INSPECTIONS');
  const isPro = useFeatureFlags('PRO');
  const hasAccessToBulletin = isBulletinEnabled && isPro;
  const isProjectPlanningEnabled = useProjectPlanningIsEnabled();

  const companyManagementDetails = compact([
    {
      name: 'manageCompanySettings',
      title: t('Manage Company Settings'),
      detail: t('View and edit organization-wide features and settings, including pay period and overtime settings.'),
    },
    {
      name: 'manageProjects',
      title: t('Manage Projects'),
      detail: t('View, create, edit, and archive projects and subprojects; create and manage project groups.'),
    },
    {
      name: 'manageCostCodes',
      title: t('Manage Cost Codes'),
      detail: t('View, create, edit, and archive cost codes; create and manage cost code groups.'),
    },
    {
      name: 'manageEquipment',
      title: t('Manage Equipment'),
      detail: t('View, create, edit, and delete equipment; view equipment reports.'),
    },
    isEquipmentInspectionsEnabled && {
      name: 'createEquipmentInspections',
      title: t('Perform Equipment Inspections'),
      detail: t(
        'Allow the employee to perform routine and ad hoc inspections (All employees can complete pre- and post-operation inspections when performing clock actions).'
      ),
      onChange: (value: boolean) => {
        if (value === false) {
          form.setData('manageEquipmentInspections', false);
        }
      },
    },
    isCertificationsEnabled && {
      name: 'manageCertificates',
      title: t('Manage Certifications'),
      detail: t('View, edit, create, and delete employee certifications and certification types.'),
    },
    {
      name: 'viewExpenses',
      title: t('View Expenses'),
      detail: t('View labor costs for employees, projects, cost codes, and equipment; view payroll information.'),
    },
    {
      name: 'manageEmployeeGroups',
      title: t('Manage Employee Groups'),
      detail: t('View, create, and manage employee groups.'),
    },
    // this is linked to schedule permissions but intentionally not disabled when those are deficient
    isProjectPlanningEnabled && {
      name: 'manageResourcePlanning',
      title: POSITION_MANAGE_PROJECT_PLAN_DETAIL_TITLE,
      detail: POSITION_MANAGE_PROJECT_PLAN_DETAIL_DESCRIPTION,
      onChange: (value: boolean) => {
        if (value) {
          form.handleChange('viewSchedule', POSITION_MASK_EVERYONE);
          form.handleChange('manageSchedule', POSITION_MASK_EVERYONE);
        }
      },
    },
    dailyReportIsEnabled && {
      name: 'dailyProjectPrompts',
      title: t('Receive Field Report Prompts'),
      detail: t('Receive reminder notification at clock out to create a field report.'),
    },
    hasAccessToBulletin && {
      name: 'manageBulletinPosts',
      title: t('Manage Company Bulletin'),
      detail: t('Add, edit, and delete their own posts from the company bulletin.'),
    },
  ]);

  return (
    <>
      <SectionHeader className="mb-6">{t('Company Management')}</SectionHeader>
      {companyManagementDetails.map((checkPermission) => (
        <div key={checkPermission.name}>
          <CheckboxFormField
            key={checkPermission.name}
            name={checkPermission.name}
            form={form}
            label={
              <HeaderText title={checkPermission.title}>
                <span>{checkPermission.detail}</span>
              </HeaderText>
            }
            disabled={
              (checkPermission.name === 'manageEmployeeGroups' && form.state.data.scopeByGroup) ||
              (checkPermission.name === 'manageIncident' && !form.state.data.createIncident)
            }
            onChange={(val) => {
              if (checkPermission.name === 'createEquipmentInspections' && val === false) {
                form.setData('manageEquipmentInspections', false);
              }
              checkPermission?.onChange?.(val);
            }}
          />

          {checkPermission.name === 'dailyProjectPrompts' &&
            dailyReportIsEnabled &&
            form.state.data.dailyProjectPrompts && (
              <DashboardRibbon theme={Theme.LIGHT} className="mb-3">
                <CheckboxFormField
                  name="dailyProjectPromptsUseSubprojects"
                  form={form}
                  label={
                    <HeaderText title={t('Assign Reports to Subprojects')}>
                      <span>
                        {t('Receive reminder notification at clock out to create a field report for each subproject.')}
                      </span>
                    </HeaderText>
                  }
                />
              </DashboardRibbon>
            )}

          {checkPermission.name === 'createEquipmentInspections' &&
            isEquipmentInspectionsEnabled &&
            form.state.data.createEquipmentInspections && (
              <DashboardRibbon theme={Theme.LIGHT} className="mb-3">
                <CheckboxFormField
                  name="manageEquipmentInspections"
                  form={form}
                  label={
                    <HeaderText title={t('Manage Equipment Inspections')}>
                      <span>{t('View, resolve, and delete equipment inspections created by others.')}</span>
                    </HeaderText>
                  }
                />
              </DashboardRibbon>
            )}
        </div>
      ))}

      {isCertificationsEnabled && (
        <div>
          <HeaderText title={t('Certifications')} />
          <Typography tag="span" className="mb-4">
            {t(
              `View, create, edit, and delete certifications. Optionally allow a supervisor to view certifications for his entire crew, but only be able to manage their own`
            )}
          </Typography>
          <CertificationsFormField form={form} className="mt-4" />
        </div>
      )}

      <HeaderText title={t('Budgets')} />
      <Typography tag="span" className="mb-4">
        {form.state.data.manageBudgets === PositionManageBudgetType.VIEW_ONLY
          ? t(
              'The employee will be able to view budget information, but won’t be able to create or edit project budgets.'
            )
          : t(
              'View, create, edit and delete project budgets. Optionally allow the user to view, but not modify, budgets.'
            )}
      </Typography>
      <div className="mt-4">
        <BudgetFormFields form={form} />
      </div>
    </>
  );
};

export default CreatePositionCompanyManagementSection;
